import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function UserGuide({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = {
    intro: 'Introduction',
    getting_started: 'Getting Started',
    dashboard: 'User Dashboard',
    job_briefing: 'Job Brief Builder',
    fact_finder: 'Fact Finder',
    contract: 'Know Your Contract',
    safety: 'Safety Rules',
    ai_assistant: 'AI Workplace Assistant',
    admin: 'Administrator Guide',
    mobile: 'Mobile Access',
    troubleshooting: 'Troubleshooting',
    faq: 'FAQ'
  };

  return (
    <div className="dashboard" data-testid="user-guide-page">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img 
            src="/stewie_front.png"
            alt="Stewie Logo"
            className="dashboard-logo-image"
          />
          Ask Stewie
        </div>
        <div className="dashboard-user">
          <button
            className="btn-nav"
            onClick={() => navigate('/')}
            data-testid="back-to-dashboard"
          >
            ← Back to Dashboard
          </button>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-role">{user.is_admin ? 'Administrator' : 'User'}</div>
          </div>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="user-guide-container">
        <aside className="guide-sidebar">
          <h3 className="sidebar-title">📖 User Guide</h3>
          <nav className="guide-nav">
            {Object.entries(sections).map(([key, label]) => (
              <button
                key={key}
                className={`guide-nav-item ${activeSection === key ? 'active' : ''}`}
                onClick={() => setActiveSection(key)}
                data-testid={`nav-${key}`}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="guide-content">
          {activeSection === 'intro' && (
            <div className="guide-section">
              <h1>Welcome to Ask Stewie™</h1>
              <p className="guide-subtitle">Your AI-Powered Union Steward Assistant</p>
              
              <h2>What is Ask Stewie?</h2>
              <p>Ask Stewie helps union members quickly find answers about:</p>
              <ul>
                <li>Union contract rules and regulations</li>
                <li>Safety procedures and guidelines</li>
                <li>Workplace rights and grievances</li>
                <li>Professional communication assistance</li>
              </ul>

              <h2>Key Features</h2>
              <div className="feature-grid">
                <div className="feature-card">
                  <span className="feature-icon">⚡</span>
                  <h3>Instant Answers</h3>
                  <p>Get immediate responses to your questions 24/7</p>
                </div>
                <div className="feature-card">
                  <span className="feature-icon">📋</span>
                  <h3>Job Brief Builder</h3>
                  <p>Create comprehensive safety job briefs conversationally</p>
                </div>
                <div className="feature-card">
                  <span className="feature-icon">📝</span>
                  <h3>Fact Finder Prep</h3>
                  <p>Prepare for workplace fact-finding meetings</p>
                </div>
                <div className="feature-card">
                  <span className="feature-icon">📚</span>
                  <h3>Multiple Libraries</h3>
                  <p>Access contract documents and safety rules</p>
                </div>
                <div className="feature-card">
                  <span className="feature-icon">🤖</span>
                  <h3>AI Assistant</h3>
                  <p>Help with emails and workplace situations</p>
                </div>
                <div className="feature-card">
                  <span className="feature-icon">📱</span>
                  <h3>Mobile Friendly</h3>
                  <p>Access from any device, anywhere</p>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'getting_started' && (
            <div className="guide-section">
              <h1>Getting Started</h1>
              
              <h2>First Time Registration</h2>
              <div className="steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h3>Visit the App</h3>
                    <p>Open your web browser and go to your organization's Ask Stewie URL, or scan the QR code on the login page.</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h3>Create Your Account</h3>
                    <p>Click "Register here" and fill in:</p>
                    <ul>
                      <li><strong>Full Name:</strong> Your name</li>
                      <li><strong>Email:</strong> Your work or personal email</li>
                      <li><strong>Department:</strong> e.g., "Electrical", "Maintenance"</li>
                      <li><strong>Password:</strong> Minimum 6 characters</li>
                    </ul>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h3>Accept Terms</h3>
                    <p>Check the box to agree to Terms of Use and Privacy Policy, then click "Create Account".</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h3>Wait for Approval</h3>
                    <p><strong>First User:</strong> Automatically becomes Administrator</p>
                    <p><strong>Other Users:</strong> Wait for admin approval before logging in</p>
                  </div>
                </div>
              </div>

              <h2>Logging In</h2>
              <ol>
                <li>Go to the Ask Stewie login page</li>
                <li>Enter your email and password</li>
                <li>Click "Sign In"</li>
              </ol>
            </div>
          )}

          {activeSection === 'dashboard' && (
            <div className="guide-section">
              <h1>User Dashboard</h1>
              
              <h2>Navigation Buttons</h2>
              <div className="nav-guide">
                <div className="nav-item">
                  <span className="nav-icon">📋</span>
                  <div>
                    <h3>Job Briefing</h3>
                    <p>Create comprehensive safety job briefs through conversation</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">📝</span>
                  <div>
                    <h3>Fact Finder + Statement</h3>
                    <p>Prepare for fact-finding meetings and generate statements</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">🦺</span>
                  <div>
                    <h3>Safety Rules</h3>
                    <p>Access safety documentation and ask safety-related questions</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">🤖</span>
                  <div>
                    <h3>AI Assistant</h3>
                    <p>Get help with emails, management responses, and grievances</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">📖</span>
                  <div>
                    <h3>User Guide</h3>
                    <p>Access this help documentation</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'job_briefing' && (
            <div className="guide-section">
              <h1>Job Brief Builder</h1>
              <p className="guide-subtitle">Conversational Safety Job Brief Assistant</p>
              
              <h2>What is the Job Brief Builder?</h2>
              <p>The Job Brief Builder helps you create comprehensive safety job briefs through a natural conversation with AskStewie. Instead of filling out long forms, you simply answer questions and Stewie builds your job brief automatically.</p>

              <h2>How to Use It</h2>
              <div className="steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h3>Start the Conversation</h3>
                    <p>Click <strong>"Job Briefing"</strong> from the dashboard. Stewie will ask: "What work will be performed today?"</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h3>Answer Questions Naturally</h3>
                    <p>Type your answers in plain language. Stewie guides you through:</p>
                    <ul>
                      <li>Job description and location</li>
                      <li>Equipment type and voltage</li>
                      <li>Scope of work and steps</li>
                      <li>Hazard identification</li>
                      <li>Energy isolation and clearances</li>
                      <li>PPE requirements</li>
                      <li>Crew assignments</li>
                      <li>Emergency plan</li>
                    </ul>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h3>Review Auto-Suggestions</h3>
                    <p>Stewie automatically suggests hazards and PPE based on your inputs. Click suggestion chips to add them, or click <strong>"Confirm All"</strong> to accept all suggestions.</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h3>Answer the Critical Question</h3>
                    <p>Stewie will ask: <strong>"What could seriously injure or kill someone on this job?"</strong> This is the most important question - think carefully about primary fatal risks.</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">5</span>
                  <div className="step-content">
                    <h3>Generate & Print</h3>
                    <p>Stewie generates a complete job brief summary. Click <strong>"Print Job Brief"</strong> to print or save as PDF.</p>
                  </div>
                </div>
              </div>

              <h2>Auto-Suggested Hazards</h2>
              <p>Stewie automatically suggests hazards based on:</p>
              <div className="info-box">
                <div className="tips-columns">
                  <div>
                    <h4>Voltage Level:</h4>
                    <ul>
                      <li>≥69kV: Arc flash, induction, MAD concerns</li>
                      <li>≥230kV: Extreme arc flash, corona discharge</li>
                    </ul>
                  </div>
                  <div>
                    <h4>Equipment Type:</h4>
                    <ul>
                      <li>Breaker: Stored spring energy</li>
                      <li>Capacitor Bank: Stored charge</li>
                      <li>CT/PT: Secondary circuit hazards</li>
                      <li>Transformer: Oil hazards, hot surfaces</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2>Example Conversation</h2>
              <div className="example-questions">
                <div className="example-q"><strong>Stewie:</strong> What work will be performed today?</div>
                <div className="example-q"><strong>You:</strong> Replace CT on 138kV breaker at Oakwood Substation</div>
                <div className="example-q"><strong>Stewie:</strong> What is the voltage level? Is the equipment energized or de-energized?</div>
                <div className="example-q"><strong>You:</strong> 138kV, de-energized</div>
              </div>

              <div className="warning-box">
                <strong>⚠️ Important:</strong> The job brief is a planning aid only. Always follow your organization's safety procedures and obtain proper clearances before beginning work.
              </div>
            </div>
          )}

          {activeSection === 'fact_finder' && (
            <div className="guide-section">
              <h1>Fact Finder + Statement Builder</h1>
              <p className="guide-subtitle">Prepare for Workplace Fact-Finding Meetings</p>
              
              <h2>What is the Fact Finder?</h2>
              <p>The Fact Finder tool helps you prepare for workplace fact-finding meetings by organizing incident details, identifying what to expect, and generating a neutral written statement.</p>

              <h2>When to Use It</h2>
              <ul>
                <li>You've been notified of an upcoming fact-finding meeting</li>
                <li>You need to document a workplace incident</li>
                <li>You want to prepare a written statement</li>
                <li>You're unsure what questions to expect</li>
              </ul>

              <h2>How to Use It</h2>
              <div className="steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h3>Click "Fact Finder + Statement"</h3>
                    <p>From the dashboard, click the Fact Finder button to open the tool.</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h3>Fill in Incident Details</h3>
                    <p>Provide information about:</p>
                    <ul>
                      <li>What happened (incident description)</li>
                      <li>When and where it occurred</li>
                      <li>Who was present</li>
                      <li>What instructions you were given</li>
                      <li>What actions you took</li>
                    </ul>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h3>Add Timeline Events</h3>
                    <p>Build a chronological timeline of events with times, actions, and who was present.</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h3>Generate Your Report</h3>
                    <p>Click "Prepare Report" to generate:</p>
                    <ul>
                      <li><strong>Meeting Preparation:</strong> What to expect, possible topics</li>
                      <li><strong>Statement Draft:</strong> A neutral, factual written statement</li>
                      <li><strong>Fact Summary:</strong> Key facts organized</li>
                      <li><strong>References:</strong> Relevant contract/policy sections</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2>The Four Output Tabs</h2>
              <div className="nav-guide">
                <div className="nav-item">
                  <span className="nav-icon">📋</span>
                  <div>
                    <h3>Meeting Preparation</h3>
                    <p>Possible topics, what to expect, suggested questions to ask</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">📝</span>
                  <div>
                    <h3>Statement Draft</h3>
                    <p>A neutral, factual written statement ready to copy</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">📊</span>
                  <div>
                    <h3>Fact Summary</h3>
                    <p>Key facts and timeline organized clearly</p>
                  </div>
                </div>

                <div className="nav-item">
                  <span className="nav-icon">📚</span>
                  <div>
                    <h3>References</h3>
                    <p>Contract and policy sections to review</p>
                  </div>
                </div>
              </div>

              <div className="info-box">
                <h3>💡 Tips for Better Results</h3>
                <ul>
                  <li>Be factual and specific - avoid opinions or assumptions</li>
                  <li>Include exact times when possible</li>
                  <li>List everyone who was present</li>
                  <li>Document exactly what instructions you received</li>
                  <li>Focus on what you observed, not what you think happened</li>
                </ul>
              </div>

              <div className="warning-box">
                <strong>⚠️ Disclaimer:</strong> This tool provides guidance only. It is not legal advice. For formal workplace proceedings, consult your union representative.
              </div>
            </div>
          )}

          {activeSection === 'contract' && (
            <div className="guide-section">
              <h1>Know Your Contract</h1>
              
              <h2>Viewing Contract Books</h2>
              <p>The main dashboard shows all uploaded union contract documents. Each book card displays:</p>
              <ul>
                <li>Union logo</li>
                <li>Book title</li>
                <li>Upload date</li>
                <li>Number of sections</li>
              </ul>

              <h2>Downloading Contract Books</h2>
              <div className="steps">
                <div className="step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h3>Hover Over Book Card</h3>
                    <p>Move your mouse over any book card to see the download overlay</p>
                  </div>
                </div>

                <div className="step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h3>Click to Download</h3>
                    <p>Click the book card and the PDF will download to your device</p>
                  </div>
                </div>
              </div>

              <h2>Asking Questions About Your Contract</h2>
              <div className="info-box">
                <h3>💡 Tips for Better Answers</h3>
                <div className="tips-columns">
                  <div>
                    <h4>✅ DO:</h4>
                    <ul>
                      <li>Ask in plain language</li>
                      <li>Be specific about your situation</li>
                      <li>Ask one question at a time</li>
                    </ul>
                  </div>
                  <div>
                    <h4>❌ DON'T:</h4>
                    <ul>
                      <li>Ask multiple unrelated questions</li>
                      <li>Expect answers not in the contract</li>
                      <li>Use contract page for safety questions</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3>Example Questions:</h3>
              <div className="example-questions">
                <div className="example-q">❓ "What are the overtime rules?"</div>
                <div className="example-q">❓ "How many sick days do I get?"</div>
                <div className="example-q">❓ "Can my manager change my schedule without notice?"</div>
              </div>
            </div>
          )}

          {activeSection === 'safety' && (
            <div className="guide-section">
              <h1>Safety Rules for Life</h1>
              
              <h2>Accessing Safety Rules</h2>
              <p>Click the <strong>🦺 Safety Rules</strong> button in the navigation to access safety documentation.</p>

              <h2>Downloading Safety Documents</h2>
              <p>Same process as contract books - hover and click to download PDFs for offline reference.</p>

              <h2>Asking Safety Questions</h2>
              <p>Type your question in the "Ask Stewie about safety rules..." box.</p>

              <h3>Example Safety Questions:</h3>
              <div className="example-questions">
                <div className="example-q">❓ "What PPE is required for electrical work?"</div>
                <div className="example-q">❓ "How do I report a safety hazard?"</div>
                <div className="example-q">❓ "What should I do if I see an unsafe condition?"</div>
              </div>

              <div className="warning-box">
                <strong>⚠️ Important:</strong> Stewie only searches safety documents on this page, ensuring accurate safety-specific answers.
              </div>
            </div>
          )}

          {activeSection === 'ai_assistant' && (
            <div className="guide-section">
              <h1>AI Workplace Assistant</h1>
              
              <p>Get professional help with workplace communication and situations.</p>

              <h2>Three Helpful Tools:</h2>

              <div className="tool-card">
                <h3>📧 Generate Email</h3>
                <p><strong>When to Use:</strong> Need to write a professional email to management, HR, or colleagues</p>
                <p><strong>How to Use:</strong></p>
                <ol>
                  <li>Click "📧 Generate Email" tab</li>
                  <li>Fill in Purpose, Recipient, Key Points, and Tone</li>
                  <li>Click "✨ Generate Email"</li>
                  <li>Review and copy the generated email</li>
                </ol>
                <p className="note"><strong>Note:</strong> Email automatically includes your name and department</p>
              </div>

              <div className="tool-card">
                <h3>💼 Management Response</h3>
                <p><strong>When to Use:</strong> Need guidance on responding to management in difficult situations</p>
                <p><strong>You'll Receive:</strong></p>
                <ul>
                  <li>Key talking points</li>
                  <li>Recommended approach</li>
                  <li>Sample phrases</li>
                  <li>Things to avoid</li>
                  <li>Best practices</li>
                </ul>
              </div>

              <div className="tool-card">
                <h3>📋 Filing Grievances</h3>
                <p><strong>When to Use:</strong> Need to file a formal grievance about workplace issues</p>
                <p><strong>You'll Receive:</strong></p>
                <ul>
                  <li>Documentation tips</li>
                  <li>Key information to include</li>
                  <li>Professional language examples</li>
                  <li>Filing process steps</li>
                  <li>Sample grievance structure</li>
                </ul>
                <p className="note"><strong>Note:</strong> This provides guidance only. For formal grievances, contact your union representative.</p>
              </div>
            </div>
          )}

          {activeSection === 'admin' && (
            <div className="guide-section">
              <h1>Administrator Guide</h1>
              <p className="admin-badge">👑 For Administrators Only</p>

              <h2>Admin Features</h2>

              <h3>Managing User Registrations</h3>
              <div className="admin-section">
                <h4>Approving Users:</h4>
                <ol>
                  <li>View "Pending User Approvals" section</li>
                  <li>Review user details (name, email, department)</li>
                  <li>Click "✓ Approve" button</li>
                  <li>User can now log in</li>
                </ol>

                <h4>Rejecting Users:</h4>
                <ol>
                  <li>Review user information</li>
                  <li>Click "✗ Reject" button</li>
                  <li>Confirm deletion</li>
                  <li>User account is permanently removed</li>
                </ol>
              </div>

              <h3>Managing Content</h3>
              <div className="admin-section">
                <h4>Uploading Books:</h4>
                <ol>
                  <li>Scroll to "Upload Book" section</li>
                  <li>Click upload area</li>
                  <li>Select PDF file (any size supported)</li>
                  <li>Click "Upload Book"</li>
                  <li>Wait for processing</li>
                </ol>

                <h4>Uploading Safety Documents:</h4>
                <ol>
                  <li>Go to "🦺 Safety Rules" page</li>
                  <li>Use "Upload Safety Rules Document" section</li>
                  <li>Documents automatically categorized as safety</li>
                </ol>

                <h4>Deleting Books:</h4>
                <ol>
                  <li>Find book card</li>
                  <li>Click "🗑️ Delete" button</li>
                  <li>Confirm deletion</li>
                </ol>
              </div>

              <div className="best-practices">
                <h3>✅ Best Practices</h3>
                <ul>
                  <li>Approve users promptly</li>
                  <li>Verify users are from your organization</li>
                  <li>Keep documents current and updated</li>
                  <li>Use descriptive file names</li>
                  <li>Never share admin credentials</li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === 'mobile' && (
            <div className="guide-section">
              <h1>Mobile Access</h1>
              
              <p>Ask Stewie works on all devices!</p>

              <h2>iPhone/iPad</h2>
              <div className="mobile-option">
                <h3>Option 1: Browser Access</h3>
                <ol>
                  <li>Open Safari or Chrome</li>
                  <li>Go to your Ask Stewie URL</li>
                  <li>Use normally like on desktop</li>
                </ol>
              </div>

              <div className="mobile-option">
                <h3>Option 2: Add to Home Screen</h3>
                <ol>
                  <li>Open site in Safari</li>
                  <li>Tap <strong>Share</strong> button (square with arrow)</li>
                  <li>Scroll down, tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong></li>
                  <li>App icon appears on home screen</li>
                </ol>
              </div>

              <h2>Android</h2>
              <div className="mobile-option">
                <h3>Option 1: Browser Access</h3>
                <ol>
                  <li>Open Chrome browser</li>
                  <li>Go to your Ask Stewie URL</li>
                  <li>Use normally</li>
                </ol>
              </div>

              <div className="mobile-option">
                <h3>Option 2: Add to Home Screen</h3>
                <ol>
                  <li>Open site in Chrome</li>
                  <li>Tap <strong>menu</strong> (three dots)</li>
                  <li>Tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong></li>
                  <li>App icon appears on home screen</li>
                </ol>
              </div>

              <div className="info-box">
                <h3>📱 Mobile Tips</h3>
                <ul>
                  <li>All features work on mobile</li>
                  <li>Rotate to landscape for easier typing</li>
                  <li>Downloads save to downloads folder</li>
                  <li>Internet connection required</li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === 'troubleshooting' && (
            <div className="guide-section">
              <h1>Troubleshooting</h1>

              <div className="troubleshoot-item">
                <h3>❌ Can't Log In</h3>
                <div className="problem-solution">
                  <div>
                    <strong>Problem:</strong> "Invalid email or password"
                  </div>
                  <div>
                    <strong>Solution:</strong>
                    <ul>
                      <li>Check email spelling</li>
                      <li>Verify password (case-sensitive)</li>
                      <li>Try resetting your password</li>
                    </ul>
                  </div>
                </div>

                <div className="problem-solution">
                  <div>
                    <strong>Problem:</strong> "Account pending approval"
                  </div>
                  <div>
                    <strong>Solution:</strong>
                    <ul>
                      <li>Wait for administrator approval</li>
                      <li>Contact your union steward</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="troubleshoot-item">
                <h3>📚 Can't See Any Books</h3>
                <div className="problem-solution">
                  <div>
                    <strong>Possible Reasons:</strong>
                    <ul>
                      <li>No books uploaded yet</li>
                      <li>On wrong page (Contract vs Safety)</li>
                      <li>Contact administrator</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="troubleshoot-item">
                <h3>❓ Questions Return "Cannot Find Information"</h3>
                <div className="problem-solution">
                  <div>
                    <strong>Try These:</strong>
                    <ul>
                      <li>Rephrase question more simply</li>
                      <li>Check you're on correct page</li>
                      <li>Verify books are uploaded</li>
                      <li>Information may not be in documents</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="troubleshoot-item">
                <h3>⚠️ Page Won't Load</h3>
                <div className="problem-solution">
                  <div>
                    <strong>Try These Steps:</strong>
                    <ul>
                      <li>Check internet connection</li>
                      <li>Refresh the page (F5)</li>
                      <li>Clear browser cache</li>
                      <li>Try different browser</li>
                      <li>Contact support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'faq' && (
            <div className="guide-section">
              <h1>Frequently Asked Questions</h1>

              <div className="faq-item">
                <h3>Q: Is Ask Stewie an app in the App Store?</h3>
                <p><strong>A:</strong> No, Ask Stewie is a web application. Access it through your browser on any device.</p>
              </div>

              <div className="faq-item">
                <h3>Q: Do I need to download anything?</h3>
                <p><strong>A:</strong> No downloads required. Just visit the URL in your web browser.</p>
              </div>

              <div className="faq-item">
                <h3>Q: Can I use it offline?</h3>
                <p><strong>A:</strong> No, internet connection is required.</p>
              </div>

              <div className="faq-item">
                <h3>Q: Is my data private?</h3>
                <p><strong>A:</strong> Yes. Your questions and data are confidential. See Privacy Policy for details.</p>
              </div>

              <div className="faq-item">
                <h3>Q: How does Stewie know the answers?</h3>
                <p><strong>A:</strong> Stewie reads uploaded union contract and safety documents, then provides answers based only on that content.</p>
              </div>

              <div className="faq-item">
                <h3>Q: Can Stewie give legal advice?</h3>
                <p><strong>A:</strong> No. Stewie provides information from your documents. For legal advice, consult your union representative.</p>
              </div>

              <div className="faq-item">
                <h3>Q: What browsers work with Ask Stewie?</h3>
                <p><strong>A:</strong> Chrome, Safari, Firefox, Edge - any modern browser.</p>
              </div>

              <div className="faq-item">
                <h3>Q: Can multiple people use it at once?</h3>
                <p><strong>A:</strong> Yes. Ask Stewie supports unlimited simultaneous users.</p>
              </div>

              <div className="contact-box">
                <h3>Need More Help?</h3>
                <p><strong>Contact:</strong> Chris.Alderson.ca@gmail.com</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default UserGuide;
